import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { SEOHead } from "@/components/SEOHead";
import { useUserQuery } from "@/hooks/useOptimizedQuery";
import { useCartMutation } from "@/hooks/useOptimisticMutation";
import { cacheKeys } from "@/lib/cache";
const Cart = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Optimized cart query
  const { data: cartItems, isLoading } = useUserQuery(
    cacheKeys.cart(user?.id || ""),
    async () => {
      if (!user) return [];
      console.log('🔄 Fetching cart items...');
      const { data } = await supabase
        .from("carts")
        .select("id, qty, products(*, shops(*))")
        .eq("user_id", user.id);
      return data ?? [];
    },
    {
      enabled: !!user,
      optimisticKey: "user_cart",
    }
  );

  // Optimistic update quantity mutation
  const updateQuantityMutation = useCartMutation(
    async ({ cartId, newQty }: { cartId: string; newQty: number }) => {
      const { error } = await supabase
        .from("carts")
        .update({ qty: newQty })
        .eq("id", cartId);
      if (error) throw error;
      return { cartId, newQty };
    },
    {
      optimisticUpdate: {
        queryKey: cacheKeys.cart(user?.id || ""),
        updater: (oldCart: any[], { cartId, newQty }) => {
          return oldCart?.map(item => 
            item.id === cartId ? { ...item, qty: newQty } : item
          ) || [];
        },
        rollbackMessage: "Failed to update quantity. Please try again.",
      },
      successMessage: "Quantity updated",
    }
  );

  // Optimistic remove item mutation
  const removeItemMutation = useCartMutation(
    async (cartId: string) => {
      const { error } = await supabase
        .from("carts")
        .delete()
        .eq("id", cartId);
      if (error) throw error;
      return cartId;
    },
    {
      optimisticUpdate: {
        queryKey: cacheKeys.cart(user?.id || ""),
        updater: (oldCart: any[], cartId: string) => {
          return oldCart?.filter(item => item.id !== cartId) || [];
        },
        rollbackMessage: "Failed to remove item. Please try again.",
      },
      successMessage: "Item removed from cart",
    }
  );

  const updateQuantity = (cartId: string, currentQty: number, delta: number) => {
    const newQty = Math.max(1, currentQty + delta);
    updateQuantityMutation.mutate({ cartId, newQty });
  };

  const removeItem = (cartId: string) => {
    removeItemMutation.mutate(cartId);
  };

  const totalAmount = cartItems?.reduce((sum, item) => {
    return sum + (item.products?.price_cents || 0) * item.qty;
  }, 0) || 0;

  if (!user) {
    return (
      <>
        <SEOHead title="Shopping Cart" description="View and manage items in your Bahama Marketplace shopping cart." />
        <main className="container mx-auto px-4 py-12">
          <div className="text-center space-y-6">
            <h1 className="text-3xl font-bold">Shopping Cart</h1>
            <p className="text-muted-foreground">Please sign in to view your cart.</p>
            <Button onClick={() => navigate("/login")}>Sign In</Button>
          </div>
        </main>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <SEOHead title="Shopping Cart" description="View and manage items in your Bahama Marketplace shopping cart." />
        <main className="container mx-auto px-4 py-12">
          <div className="space-y-8">
            <h1 className="text-3xl font-bold">Shopping Cart</h1>
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-6 border border-border/40 rounded-lg animate-pulse">
                  <div className="w-20 h-20 bg-muted rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-48"></div>
                    <div className="h-3 bg-muted rounded w-32"></div>
                  </div>
                  <div className="h-8 bg-muted rounded w-24"></div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!cartItems?.length) {
    return (
      <>
        <SEOHead title="Shopping Cart" description="Your Bahama Marketplace shopping cart is currently empty." />
        <main className="container mx-auto px-4 py-12">
          <div className="text-center space-y-6">
            <h1 className="text-3xl font-bold">Your Cart is Empty</h1>
            <p className="text-muted-foreground">Discover amazing products from local Bahamian vendors.</p>
            <Button onClick={() => navigate("/")}>Continue Shopping</Button>
          </div>
        </main>
      </>
    );
  }
  return (
    <>
      <SEOHead
        title="Shopping Cart - Bahama Marketplace"
        description="Review your items and checkout securely on Bahama Marketplace. Fast shipping from local Bahamian sellers."
      />
      <main className="container mx-auto px-4 py-12">
        <div className="space-y-8">
          <h1 className="text-3xl font-bold">Shopping Cart</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item: any) => (
                <div key={item.id} className="flex items-center gap-4 p-6 border border-border/40 rounded-lg bg-card">
                  <div className="w-20 h-20 rounded overflow-hidden bg-muted">
                    {item.products?.image_url ? (
                      <img
                        src={item.products.image_url}
                        alt={item.products.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                        No image
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-semibold">{item.products?.name}</h3>
                    {item.products?.shops && (
                      <p className="text-sm text-muted-foreground">by {item.products.shops.name}</p>
                    )}
                    <p className="text-lg font-bold mt-1">
                      BSD {((item.products?.price_cents || 0) / 100).toFixed(2)}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.id, item.qty, -1)}
                      disabled={updateQuantityMutation.isUpdating}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>

                    <span className="w-8 text-center font-medium">{item.qty}</span>

                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.id, item.qty, 1)}
                      disabled={updateQuantityMutation.isUpdating}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 ml-2"
                      onClick={() => removeItem(item.id)}
                      disabled={removeItemMutation.isUpdating}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="space-y-6">
              <div className="p-6 border border-border/40 rounded-lg bg-card space-y-4">
                <h2 className="text-xl font-semibold">Order Summary</h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal ({cartItems.reduce((sum, item) => sum + item.qty, 0)} items)</span>
                    <span>BSD {(totalAmount / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>BSD {(totalAmount / 100).toFixed(2)}</span>
                  </div>
                </div>
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={() => navigate("/checkout")}
                  disabled={!cartItems.length}
                >
                  Proceed to Checkout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};
export default Cart;